import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { UpdateMedicalRecordRequestPayload } from "../../api/medical-record-requests.api";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import Input from "../../components/shared/input";
import { Modal } from "../../components/shared/modal/modal";
import ThemedPhoneInput from "../../components/shared/themed-phone-input";
import { useMedicalRecordRequestDetailContext } from "../context/MedicalRecordRequestDetailContext";

const initialFormValues: UpdateMedicalRecordRequestPayload = {
  recipientName: null,
  deliveryMethod: null,
  faxNumber: null,
  emailAddress: null,
};

export function EditContactModal() {
  const {
    isEditOpen,
    closeEdit,
    getEditInitialValues,
    handleSave,
    isUpdatingRequest,
  } = useMedicalRecordRequestDetailContext();
  const [formValues, setFormValues] =
    useState<UpdateMedicalRecordRequestPayload>(initialFormValues);

  // Reset form when modal opens with initial values
  useEffect(() => {
    if (isEditOpen) {
      const initialValues = getEditInitialValues();
      setFormValues(initialValues ?? initialFormValues);
    }
  }, [isEditOpen, getEditInitialValues]);

  const handleChange =
    (field: keyof UpdateMedicalRecordRequestPayload) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({
        ...prev,
        [field]: event.target.value || null,
      }));
    };

  const handlePhoneChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      faxNumber: value || null,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await handleSave(formValues);
    } catch (err) {
      console.error("Unable to save contact information", err);
    }
  };

  const handleClose = () => {
    setFormValues(initialFormValues);
    closeEdit();
  };

  return (
    <Modal
      isOpen={isEditOpen}
      onClose={handleClose}
      title="Edit Contact Information"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Institution"
          value={formValues.recipientName ?? ""}
          onChange={handleChange("recipientName")}
          fullWidth
        />
        <Input
          label="Delivery Method"
          value={formValues.deliveryMethod ?? ""}
          onChange={handleChange("deliveryMethod")}
          fullWidth
        />
        <ThemedPhoneInput
          label="Fax Number"
          value={formValues.faxNumber ?? ""}
          onChange={handlePhoneChange}
          fullWidth
        />
        <Input
          label="Email Address"
          value={formValues.emailAddress ?? ""}
          onChange={handleChange("emailAddress")}
          fullWidth
          type="email"
        />
        <div className="flex justify-end gap-2">
          <SubmitButton
            type="button"
            color="beige"
            onClick={handleClose}
            hideArrow
          >
            Cancel
          </SubmitButton>
          <SubmitButton type="submit" isLoading={isUpdatingRequest}>
            Save
          </SubmitButton>
        </div>
      </form>
    </Modal>
  );
}
